<script setup>
import QuotationActionsCell from "@frontJs/components/tables/cells/projectManagement/apikom/QuotationActionsCell.vue";
import QuotationDetailsCell from "@frontJs/components/tables/cells/projectManagement/apikom/QuotationDetailsCell.vue";
import { PlusIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import DangerIconCell from "@js/components/tables/cells/DangerIconCell.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  userSessionKey: {
    type: String,
    required: false,
    default: null,
  },
  ongoing: {
    type: Boolean,
    required: false,
    default: false,
  },
  won: {
    type: Boolean,
    required: false,
    default: false,
  },
  toInvoice: {
    type: Boolean,
    required: false,
    default: false,
  },
  invoiced: {
    type: Boolean,
    required: false,
    default: false,
  },
  partiallyInvoiced: {
    type: Boolean,
    required: false,
    default: false,
  },
  paid: {
    type: Boolean,
    required: false,
    default: false,
  },
  closed: {
    type: Boolean,
    required: false,
    default: false,
  },
  lost: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  data,
  count,
  pagination,
  pageCount,
  sorting,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
  updateData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "id",
      header: () => t("label.id"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.id,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "date",
      header: () => t("label.date"),
      cell: (props) =>
        h(TextCellV2, {
          text: formatDate(props.row.original.date),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "customer",
      header: () => t("label.customer"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.customer,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "type",
      header: () => t("label.type"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.type,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "users",
      header: () => t("label.responsible"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.users
            .map((user) => user.fullName)
            .join(", "),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "sentAt",
      header: () => t("label.sentAt"),
      cell: (props) =>
        props.row.original.sentAt
          ? h(TextCellV2, {
              text: formatDate(props.row.original.sentAt),
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "calledAt",
      header: () => t("label.lastReminder"),
      cell: (props) =>
        props.row.original.calledAt
          ? h(TextCellV2, {
              text: formatDate(props.row.original.calledAt),
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "toCall",
      header: () => t("label.toCall"),
      cell: (props) => (props.row.original.toCall ? h(DangerIconCell) : null),
    }),
    columnHelper.display({
      id: "details",
      header: t("label.details"),
      cell: (innerProps) =>
        h(QuotationDetailsCell, {
          row: innerProps.row,
        }),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(QuotationActionsCell, {
          row: innerProps.row,
          callback: () => updateData(),
        }),
    }),
  ],
  url: route("front.projectManagement.apikom.postQuotations"),
  data: {
    ongoing: props.ongoing,
    won: props.won,
    toInvoice: props.toInvoice,
    invoiced: props.invoiced,
    partiallyInvoiced: props.partiallyInvoiced,
    paid: props.paid,
    closed: props.closed,
    lost: props.lost,
  },
  userSessionKey: props.userSessionKey,
  columnPinning: {
    right: ['actions']
  }
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :filters="filters"
      :available-filters="availableFilters"
      :set-filters="setFilters"
      :set-global-filter="setGlobalFilter"
    >
      <template #actions>
        <ActionButton
          size="small"
          :href="route('front.projectManagement.apikom.createQuotation')"
          :left-icon="PlusIcon"
        >
          {{ t("button.add") }}
        </ActionButton>
      </template>
    </DataTableLayout>
  </div>
</template>
